import { FormProvider } from '@formily/react';
import { Form } from '@formily/antd-v5';
import {
  createForm,
  type Form as FormType,
  type IFormProps,
} from '@formily/core';
import { Button, Space } from 'antd';
import { useMemo, useRef } from 'react';

import type { IFormilySchema } from '../../types';
import { findFormComponent } from '../../utils/findFormComponent';
import { injectRequiredValidators } from '../../utils/injectRequiredValidators';
import { requiredIgnoringDeletedEffect } from '../../utils/formEffects';
import { parseJSONSchema } from '../../utils/data-parser';
import { usePage } from '../../context/PageContext';
import { useTranslate } from '../../../translate';
import xReactionParser from '../../utils/xReactionParser';
import { escapeFormItemsSchemas } from '../../utils';
import createSchemaFields from '../../utils/createSchemaFields';
import { stripSkipOnSubmitFields } from '../../utils/stripSkipOnSubmitFields';
import { useReactionScope } from '../../hooks/useReactionScope';

interface IAddItemFormProps {
  parentForm: FormType;
  schema: IFormilySchema;
  onSubmit: (values: Record<string, unknown>) => void;
  onCancel: () => void;
  initialValues?: Record<string, unknown>;
  context: Record<string, unknown>;
  cancelButtonText?: string;
  saveButtonText?: string;
}

const AddItemForm: React.FC<IAddItemFormProps> = ({
  schema,
  onSubmit,
  onCancel,
  initialValues = {},
  parentForm,
  context,
  cancelButtonText = 'Cancel',
  saveButtonText = 'Save',
}) => {
  const FormSchemaField = useRef(createSchemaFields()).current;
  const reactionScope = useReactionScope();

  const form = createForm({
    initialValues,
    parent: parentForm,
    context,
    effects: requiredIgnoringDeletedEffect,
  } as IFormProps<any>);

  const pageData = usePage();
  const { t } = useTranslate();

  const handleSubmit = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();

    try {
      await form.submit();
      const values = stripSkipOnSubmitFields(form);
      onSubmit(values);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    onCancel();
  };

  const formSchema = findFormComponent(schema);

  const parsedSchema = useMemo(() => {
    if (formSchema)
      return parseJSONSchema(
        formSchema,
        {
          ...pageData.context,
          item: initialValues,
        },
        { t }
      );
    return formSchema;
  }, [schema, pageData.context, t]);

  const processedSchema = useMemo(
    () =>
      escapeFormItemsSchemas(xReactionParser(parsedSchema as IFormilySchema)),
    [parsedSchema]
  );

  if (!processedSchema) {
    return <div>Invalid schema: Form component not found</div>;
  }

  const schemaWithValidators = injectRequiredValidators(processedSchema);

  return (
    <FormProvider form={form}>
      <Form form={form} layout="vertical">
        <FormSchemaField
          schema={schemaWithValidators as any}
          scope={reactionScope}
        />
        <Space
          style={{ marginTop: 16, width: '100%', justifyContent: 'flex-end' }}
        >
          <Button onClick={handleCancel}>{cancelButtonText}</Button>
          <Button type="primary" onClick={handleSubmit}>
            {saveButtonText}
          </Button>
        </Space>
      </Form>
    </FormProvider>
  );
};

export default AddItemForm;
